/**
 * Modal "Participar da Promoção" — espelha a 2ª tela do Mercado Turbo que ela mandou (13/09/2026):
 * preço final em R$ e desconto em % lado a lado, cada um com setinha ▲▼ (pedido dela, 17/09);
 * digitar num atualiza o outro e nenhum sai da faixa que a API aceita. "Preço Meli+" fica como
 * NÃO INFORMADO: não vem de /seller-promotions/items e a regra é nunca inventar dado.
 *
 * A M.C. aqui é ESTIMATIVA (regra do degrau) — com o serviço no ar o frete é consultado de verdade
 * no preço escolhido (o ML cobra frete em degraus por faixa de preço).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography, useTheme } from '@mui/material'
import {
  ACEITE_DIRETO, UNIDADES_RELAMPAGO, classeMc, clampPreco, mcNoPreco, payloadAtivar, promoRange, selKey, priceToRange, unidadesRelampago,
  type Anuncio, type Promocao,
} from './logic'
import type { ResultadoEscrita } from './promocoesService'
import { usePromo } from './ctx'
import { AdCell, LARANJA, brl, mono, num, pct, useCorMc } from './ui'

const r2 = (n: number) => Math.round(n * 100) / 100
const r1 = (n: number) => Math.round(n * 10) / 10

function Spin({ label, value, step, disabled, onChange, onSpin, largura }: {
  label: string; value: string; step: number; disabled: boolean; largura: number
  onChange: (v: string) => void; onSpin: (dir: 1 | -1) => void
}) {
  return (
    <Stack spacing={0.4} alignItems="center">
      <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled' }}>{label}</Typography>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <TextField size="small" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
          slotProps={{ htmlInput: { inputMode: 'decimal', step, style: { textAlign: 'center', fontWeight: 600, fontSize: 15, width: largura, paddingRight: 26 } } }} />
        {!disabled ? (
          <Stack sx={{ position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)', gap: '1px' }}>
            <Box component="button" type="button" onClick={() => onSpin(1)} aria-label={`Aumentar ${label}`}
              sx={{ width: 18, height: 15, p: 0, lineHeight: 1, cursor: 'pointer', border: 1, borderColor: 'divider', borderRadius: 1, fontSize: 9, bgcolor: 'action.hover', color: 'text.secondary', '&:hover': { color: 'primary.main', borderColor: 'primary.main' } }}>▲</Box>
            <Box component="button" type="button" onClick={() => onSpin(-1)} aria-label={`Diminuir ${label}`}
              sx={{ width: 18, height: 15, p: 0, lineHeight: 1, cursor: 'pointer', border: 1, borderColor: 'divider', borderRadius: 1, fontSize: 9, bgcolor: 'action.hover', color: 'text.secondary', '&:hover': { color: 'primary.main', borderColor: 'primary.main' } }}>▼</Box>
          </Stack>
        ) : null}
      </Box>
    </Stack>
  )
}

export function ModalParticipar({ a, p, onClose }: { a: Anuncio; p: Promocao; onClose: () => void }) {
  const ctx = usePromo()
  const t = useTheme()
  const cor = useCorMc()
  const k = selKey(a, p)
  const range = useMemo(() => promoRange(p), [p])
  const editavel = !!range
  const orig = p.original_price ?? a.preco_atual
  const precoInicial = (() => {
    const v = ctx.pickerVal.get(k)
    if (range && v != null) return clampPreco(range, range.unit === '%' ? r2(range.original * (1 - v / 100)) : v)
    return p.preco_promo ?? p.suggested_discounted_price ?? orig
  })()

  const [preco, setPreco] = useState<number>(precoInicial)
  const [rsTxt, setRsTxt] = useState(precoInicial.toFixed(2))
  const [pctTxt, setPctTxt] = useState(orig ? String(r1((1 - precoInicial / orig) * 100)) : '')
  // Relâmpago: quantas unidades entram na oferta (obrigatório no POST). Regra dela: 5, dentro do
  // min/max que a API devolve. Editável aqui; na massa vai o `unidades` da fila do motor.
  const relampago = p.type === 'LIGHTNING'
  const unidMin = p.stock_min ?? UNIDADES_RELAMPAGO
  const unidMax = p.stock_max ?? (a.estoque > 0 ? a.estoque : UNIDADES_RELAMPAGO)
  const [unid, setUnid] = useState<number>(unidadesRelampago(a, p))
  const [unidTxt, setUnidTxt] = useState(String(unidadesRelampago(a, p)))
  const aplicarUnid = (n: number) => {
    const v = Math.max(unidMin, Math.min(Math.floor(Number.isFinite(n) ? n : unid), unidMax))
    setUnid(v); setUnidTxt(String(v))
  }
  const [frete, setFrete] = useState<number | null>(null)
  const [armado, setArmado] = useState(false)
  const [busy, setBusy] = useState(false)
  const [res, setRes] = useState<{ ok: boolean; r: ResultadoEscrita } | null>(null)
  const [undo, setUndo] = useState<ResultadoEscrita | null>(null)
  const seq = useRef(0)

  // frete REAL no preço escolhido, só com o serviço no ar (debounce curto)
  useEffect(() => {
    if (!ctx.escreve || preco <= 0) { setFrete(null); return }
    const meu = ++seq.current
    const h = window.setTimeout(() => {
      void ctx.freteReal(a.mlb, preco).then((v) => { if (seq.current === meu) setFrete(v) })
    }, 350)
    return () => window.clearTimeout(h)
  }, [preco, a.mlb, ctx])

  const aplicar = (novo: number, de: 'rs' | 'pct' | 'spin') => {
    let v = Number.isFinite(novo) ? novo : preco
    if (range) v = clampPreco(range, v)
    v = r2(v)
    setPreco(v)
    if (de !== 'rs') setRsTxt(v.toFixed(2))
    if (de !== 'pct' && orig) setPctTxt(String(r1((1 - v / orig) * 100)))
    if (range) ctx.setPicker(k, priceToRange(range, v))
  }

  const est = mcNoPreco(a, p, preco, ctx.base, ctx.cmvManual, frete)
  const classe = classeMc(est, ctx.alvo)
  const body = payloadAtivar(ctx.contaId, a, p, preco, new Date(), relampago ? unid : undefined)

  const participar = async () => {
    if (!(preco > 0)) return
    if (!ctx.escreve) {
      ctx.marcar(k, 'started', true)
      ctx.toggleSel(k, true)
      onClose()
      return
    }
    if (!armado) { setArmado(true); return }
    setBusy(true)
    const r = await ctx.ativar(a, p, preco, relampago ? unid : undefined)
    setBusy(false)
    setArmado(false)
    setRes({ ok: r.ok, r })
  }

  const desfazer = async () => {
    setBusy(true)
    const d = await ctx.remover(a, p)
    setBusy(false)
    setUndo(d)
  }

  const off = orig ? r1((1 - preco / orig) * 100) : null
  const minmax = !range
    ? (p.preco_promo != null
      ? 'Esta campanha não oferece faixa de valor na API — só aceite pelo preço definido.'
      : 'A API não devolveu preço nem faixa para esta promoção.')
    : null

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: `${t.palette.primary.main}22`, fontSize: 17 }}>%</Box>
        <Box sx={{ flex: 1, fontSize: 18, fontWeight: 600 }}>
          Participar da Promoção{' '}
          <Box component="span" sx={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '.05em', borderRadius: 99, px: 0.9, ml: 0.5, verticalAlign: 'middle',
            border: `1px ${ctx.escreve ? 'solid' : 'dashed'} ${ctx.escreve ? t.palette.error.main : t.palette.text.disabled}`, color: ctx.escreve ? t.palette.error.main : t.palette.text.disabled }}>
            {ctx.escreve ? (ctx.live?.dry_run ? 'teste' : 'escrita real') : 'preview'}
          </Box>
        </Box>
        <IconButton aria-label="Fechar" onClick={onClose} sx={{ position: 'absolute', right: 10, top: 12 }}>×</IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pb: 1.5, borderBottom: 1, borderColor: 'divider', borderBottomStyle: 'dashed' }}>
          <AdCell a={a} />
        </Box>
        <Typography sx={{ textAlign: 'center', fontWeight: 600, fontSize: 14.5, my: 1.5 }}>{p.name || p.type_label} — {p.type_label}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.25, textAlign: 'center' }}>
          <Box><Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Preço Original</Typography><Typography sx={{ fontSize: 15, fontWeight: 600 }}>{brl(orig)}</Typography></Box>
          <Box><Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Preço Promocional Atual</Typography>
            <Typography sx={{ fontSize: p.preco_promo == null ? 13 : 15, fontWeight: p.preco_promo == null ? 400 : 600, fontStyle: p.preco_promo == null ? 'italic' : 'normal', color: p.preco_promo == null ? 'text.disabled' : undefined }}>{p.preco_promo == null ? 'não configurado' : brl(p.preco_promo)}</Typography></Box>
          <Box><Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Preço Meli+</Typography><Typography sx={{ fontSize: 13, fontStyle: 'italic', color: 'text.disabled' }}>não informado pela API</Typography></Box>
        </Box>

        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ my: 2.5, color: 'text.disabled', '&::before, &::after': { content: '""', flex: 1, height: 1, bgcolor: 'divider' } }}>
          <Box component="span" sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 99, px: 1.5, py: 0.35, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>Preço Promocional</Box>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, alignItems: 'start' }}>
          <Box>
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary', textAlign: 'center', mb: 1 }}>{editavel ? 'Escolha em R$ ou em %' : 'Valor definido pela campanha'}</Typography>
            <Stack direction="row" spacing={1.25} justifyContent="center">
              <Spin label="Preço final" value={rsTxt} step={0.5} disabled={!editavel} largura={120}
                onChange={(v) => { setRsTxt(v); const n = parseFloat(v.replace(',', '.')); if (!Number.isNaN(n)) aplicar(n, 'rs') }}
                onSpin={(dir) => aplicar((Number.isFinite(preco) ? preco : orig) + dir * ((range?.unit === 'R$' && range.step) || 0.5), 'spin')} />
              <Spin label="Desconto %" value={pctTxt} step={0.5} disabled={!editavel || !orig} largura={100}
                onChange={(v) => { setPctTxt(v); const d = parseFloat(v.replace(',', '.')); if (!Number.isNaN(d) && orig) aplicar(r2(orig * (1 - d / 100)), 'pct') }}
                onSpin={(dir) => { if (!orig) return; const base = parseFloat(pctTxt.replace(',', '.')); const d = (Number.isNaN(base) ? 0 : base) + dir * ((range?.unit === '%' && range.step) || 0.5); aplicar(r2(orig * (1 - d / 100)), 'spin') }} />
            </Stack>
            <Typography sx={{ textAlign: 'center', fontSize: 12, color: 'text.disabled', mt: 1.25, lineHeight: 1.7 }}>
              {range ? (
                <>Mínimo: <b>{brl(range.precoMin)}</b> &nbsp; Máximo: <b>{brl(range.precoMax)}</b><br />Preço Sugerido pelo ML: <b>{p.suggested_discounted_price != null ? brl(p.suggested_discounted_price) : 'não informado'}</b></>
              ) : <Box component="span" sx={{ color: p.preco_promo == null ? LARANJA : undefined }}>{minmax}</Box>}
            </Typography>
            {relampago ? (
              <Box sx={{ mt: 1.5, pt: 1.25, borderTop: 1, borderColor: 'divider', borderTopStyle: 'dashed' }}>
                <Stack direction="row" spacing={1.25} justifyContent="center">
                  <Spin label="Unidades na oferta" value={unidTxt} step={1} disabled={false} largura={100}
                    onChange={(v) => { setUnidTxt(v); const n = parseInt(v, 10); if (!Number.isNaN(n)) aplicarUnid(n) }}
                    onSpin={(dir) => aplicarUnid(unid + dir)} />
                </Stack>
                <Typography sx={{ textAlign: 'center', fontSize: 12, color: 'text.disabled', mt: 1, lineHeight: 1.6 }}>
                  O ML exige entre <b>{unidMin}</b> e <b>{unidMax}</b> unidades{p.stock_min == null ? ' (mínimo da regra; a API não informou)' : ''}. Quando acabam, a oferta encerra sozinha.
                  Regra da Fiesta: <b>{UNIDADES_RELAMPAGO}</b>.
                </Typography>
              </Box>
            ) : null}
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 1 }}>Margem de Contribuição</Typography>
            {est.rs == null ? (
              <>
                <Typography sx={{ fontSize: 30, fontWeight: 600, color: LARANJA }}>—</Typography>
                <Typography sx={{ fontSize: 11.5, color: 'warning.main', maxWidth: '34ch', mx: 'auto', lineHeight: 1.45 }}>
                  Não dá para calcular: falta {est.cmv == null ? 'o custo deste anúncio' : 'comissão ou frete real'}.{est.cmv == null ? ' Informe o CMV na aba "Por anúncio" para ver a margem aqui.' : ''}
                </Typography>
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: 30, fontWeight: 600, letterSpacing: '-.02em', lineHeight: 1.15, color: cor(classe), ...num }}>{brl(est.rs)}</Typography>
                <Typography sx={{ fontSize: 16, color: cor(classe) }}>{pct(est.pct)}</Typography>
                {est.semImposto ? <Typography sx={{ fontSize: 11.5, color: LARANJA, mt: 0.75, maxWidth: '34ch', mx: 'auto', lineHeight: 1.45 }}>⚠ Sem imposto na conta — este anúncio não vendeu no período, então não há alíquota efetiva real. A margem acima está otimista.</Typography> : null}
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.75, maxWidth: '36ch', mx: 'auto', lineHeight: 1.45 }}>
                  Comissão pela regra do degrau — estimativa. Frete: {est.freteOrigem}{est.freteAproximado ? ' (pode mudar de degrau)' : ''}.
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {ctx.escreve && armado && !res ? (
          <Box sx={{ mt: 2, fontSize: 13, lineHeight: 1.5 }}>
            <b>Vai ser enviado agora, de verdade, para a conta {ctx.live?.nickname || ctx.contaId}:</b>
            <Box sx={{ ...mono, fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', bgcolor: 'action.hover', border: 1, borderColor: 'divider', borderRadius: 2, p: 1.25, my: 0.75 }}>
              {`POST /seller-promotions/items/${a.mlb}?app_version=v2\n${JSON.stringify(body, null, 1)}`}
            </Box>
            O anúncio <b>{a.mlb}</b> passa de {brl(orig)} para <b>{brl(preco)}</b>{off != null ? ` (−${off}%)` : ''} no Mercado Livre, visível para quem comprar. Clique de novo para confirmar, ou Fechar para desistir.
            {ACEITE_DIRETO.has(p.type) ? <Box sx={{ color: 'text.secondary', mt: 0.5 }}>Este tipo tem valor definido pelo ML: vai só a campanha, sem preço.</Box> : null}
            {relampago ? <Box sx={{ color: LARANJA, mt: 0.5 }}>Relâmpago com <b>{unid}</b> unidade(s) reservada(s). ⚠ Pela documentação do ML, oferta relâmpago já ativada não pode ser removida — só pausando o anúncio.</Box> : null}
          </Box>
        ) : null}

        {res ? (
          <Box sx={{ mt: 2, fontSize: 13, lineHeight: 1.5 }}>
            {res.ok ? (
              <>
                <Typography sx={{ color: 'success.main', fontWeight: 600 }}>✓ Promoção {res.r.simulado ? 'SIMULADA (teste — nada foi ao ML)' : 'ativada de verdade'} — HTTP {res.r.http}.</Typography>
                <Box sx={{ ...mono, fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', bgcolor: 'action.hover', border: 1, borderColor: 'divider', borderRadius: 2, p: 1.25, my: 0.75 }}>{JSON.stringify(res.r.resposta)}</Box>
                Registrado em escritas_promo.log.
                {!undo ? <Box sx={{ mt: 1 }}><Button size="small" variant="outlined" disabled={busy} onClick={() => void desfazer()}>Remover esta promoção do anúncio</Button></Box>
                  : <Typography sx={{ mt: 1, color: undo.ok ? 'success.main' : LARANJA }}>{undo.ok ? `✓ Removida — HTTP ${undo.http}.` : `Não removeu — HTTP ${undo.http}: ${undo.erro ?? JSON.stringify(undo.resposta)}`}</Typography>}
              </>
            ) : (
              <>
                <Typography sx={{ color: LARANJA, fontWeight: 600 }}>O ML recusou — HTTP {res.r.http ?? 0}.</Typography>
                <Box sx={{ ...mono, fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', bgcolor: 'action.hover', border: 1, borderColor: 'divider', borderRadius: 2, p: 1.25, my: 0.75 }}>{res.r.erro ?? JSON.stringify(res.r.resposta)}</Box>
                Nada mudou no anúncio.
              </>
            )}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2.5, gap: 1 }}>
        {!res?.ok ? (
          <Button variant={armado ? 'contained' : 'outlined'} color={armado ? 'error' : 'success'} disabled={busy || !(preco > 0)} onClick={() => void participar()}
            sx={{ px: 3, fontWeight: armado ? 700 : 600, borderStyle: ctx.escreve ? 'solid' : 'dashed' }}>
            {busy ? 'enviando…' : armado ? '⚠ Confirmar e escrever na API' : `✓ Participar${ctx.escreve ? '' : ' (preview)'}`}
          </Button>
        ) : null}
        <Button variant="outlined" color="inherit" onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}
